/* Lançamentos e envios fictícios do modo demonstração (gerados com semente fixa). */

import { CATEGORIAS_BACKEND, DEMO_CATALOGO, MATERIAIS } from './catalogo';
import type { Envio, Lancamento } from '../tipos/api';

export interface LinhaDemo extends Lancamento {
  id_material: number | null;
  quantidade: number | null;
  valor_total: number;
}

interface NovaLinha {
  id: string;
  data: string;
  descricao: string;
  cnpj_fornecedor?: string;
  id_categoria: number;
  id_unidade: string;
  total: number;
  id_material?: number | null;
  quantidade?: number | null;
  valor_total?: number;
}

export function novaLinha(l: NovaLinha): LinhaDemo {
  const [categoria, categoria_geral, escopo] = CATEGORIAS_BACKEND[l.id_categoria];
  const obra = DEMO_CATALOGO.obras.find((o) => o.id === l.id_unidade)?.nome || 'Sem obra informada';
  const fugitiva = categoria_geral === 'Emissões Fugitivas';
  const soCo2 = l.id_categoria === 14 || l.id_categoria === 38;
  const co2 = fugitiva ? 0 : l.total * (soCo2 ? 1 : 0.985);
  const ch4 = fugitiva || soCo2 ? 0 : (l.total * 0.006) / 28;
  const n2o = fugitiva || soCo2 ? 0 : (l.total * 0.009) / 265;
  const biogenica = [4, 2, 1, 29, 25].includes(l.id_categoria) ? l.total * 0.14 : 0;
  return {
    id: l.id, data: l.data, descricao: l.descricao, cnpj_fornecedor: l.cnpj_fornecedor || '', id_categoria: l.id_categoria,
    categoria, categoria_geral, escopo, id_unidade: l.id_unidade, obra, co2, ch4, n2o, total: +l.total.toFixed(4), biogenica,
    id_material: l.id_material ?? null, quantidade: l.quantidade ?? null, valor_total: l.valor_total || 0,
  };
}

const FONTES = [
  { cat: 4, base: 3.2, desc: 'Óleo diesel — geradores do canteiro', forn: '11.482.310/0001-77' },
  { cat: 2, base: 5.1, desc: 'Óleo diesel — escavadeiras e retroescavadeiras', forn: '11.482.310/0001-77' },
  { cat: 1, base: 1.4, desc: 'Caminhonetes da equipe de obra', forn: '11.482.310/0001-77' },
  { cat: 5, base: 1.8, desc: 'Recarga de R-410A — ar-condicionado', forn: '23.019.554/0001-02', prob: 0.3 },
  { cat: 14, base: 4.6, desc: 'Energia elétrica — ligação provisória', forn: '34.771.208/0001-15' },
  { cat: 29, base: 2.6, desc: 'Frete de concreto e aço — diesel', forn: '45.906.117/0001-60' },
  { cat: 25, base: 1.4, desc: 'Retirada de entulho — caçambas', forn: '56.338.402/0001-91' },
  { cat: 38, base: 14.5, desc: 'Concreto usinado e aço — notas de compra', forn: '67.214.580/0001-33' },
];

let seq = 1000;
export const proximoId = () => `doc-${++seq}`;

/** 2025 inteiro e jan–set/2026, para cada obra e fonte, com variação pseudoaleatória estável. */
export const LANCAMENTOS: LinhaDemo[] = (() => {
  let semente = 20260301;
  const rnd = () => (semente = (semente * 1664525 + 1013904223) % 4294967296) / 4294967296;
  const pesoObra = [1.25, 0.5, 0.95, 1.6, 0.8];
  const linhas: LinhaDemo[] = [];
  ([[2025, 12, 1.08], [2026, 9, 1]] as const).forEach(([ano, meses, fator]) => {
    for (let m = 0; m < meses; m++) {
      const tendencia = fator * (1 - m * 0.012) * (ano === 2026 && m === 8 ? 0.8 : 1);
      DEMO_CATALOGO.obras.forEach((obra, oi) =>
        FONTES.forEach((f) => {
          if (f.prob && rnd() > f.prob) return;
          const total = f.base * pesoObra[oi] * tendencia * (0.8 + rnd() * 0.4) * (f.prob ? 1.2 : 1);
          const mat = f.cat === 38 ? MATERIAIS[[4, 0, 3, 6][Math.floor(rnd() * 4)]] : null;
          linhas.push(novaLinha({
            id: proximoId(),
            ...(mat ? { id_material: mat.id, quantidade: (total * 1000) / mat.fe } : {}),
            data: `${ano}-${String(m + 1).padStart(2, '0')}-${String(2 + Math.floor(rnd() * 25)).padStart(2, '0')}`,
            descricao: f.desc, cnpj_fornecedor: f.forn, id_categoria: f.cat, id_unidade: obra.id, total,
          }));
        }),
      );
    }
  });
  return linhas;
})();

/** Envios do modo demonstração, no mesmo formato de GET /wl/envios. */
export const ENVIOS: Envio[] = [
  { id: 'env-3', created_at: '2026-02-25T11:05:00', tipo: 'link', origem: 'https://cte.sharepoint.com/sites/Obras /Residencial Aurora/Notas Fevereiro', status: 'em_processamento', mensagem: 'Previsão: até 3 dias úteis', arquivos: [], id_unidade: 'obra-4', obra: 'Residencial Aurora', tipo_documento: 'material' },
  { id: 'env-2', created_at: '2026-02-20T09:12:00', tipo: 'planilha', origem: 'modelo_emissoes_fev2026.xlsx', categoria: 'combustao-movel-2', status: 'importado', mensagem: '42 lançamento(s) gravado(s)', arquivos: [] },
  { id: 'env-1', created_at: '2026-02-15T16:40:00', tipo: 'documentos', origem: '8 arquivo(s): NF 1021.pdf, NF 1022.pdf, NF 1023.pdf…', status: 'concluido', mensagem: 'Processado em 18/02', arquivos: [], id_unidade: 'obra-1', obra: 'Edifício Corporate Tower', tipo_documento: 'combustivel' },
];

export function registrarEnvio(e: Omit<Envio, 'id' | 'created_at' | 'arquivos'> & Partial<Envio>): Envio {
  const envio: Envio = { id: `env-${Date.now()}`, created_at: new Date().toISOString(), arquivos: [], ...e };
  ENVIOS.unshift(envio);
  return envio;
}
